import { useState, useCallback, ReactNode } from 'react'
import {
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline'
import clsx from 'clsx'

// --- Types ---

export interface ColumnDef<T> {
  /** Unique key, used for sorting */
  key: string
  /** Header label */
  header: string
  /** Render cell content */
  cell: (row: T) => ReactNode
  /** Allow sorting on this column */
  sortable?: boolean
  /** Column alignment */
  align?: 'left' | 'center' | 'right'
  /** Hide on mobile (shown in card renderer instead) */
  hideOnMobile?: boolean
  /** Column width class */
  width?: string
}

export interface BulkAction {
  label: string
  icon?: ReactNode
  variant?: 'primary' | 'danger'
  onAction: (selectedIds: string[]) => void
}

export interface RowAction<T> {
  label: string
  icon?: ReactNode
  onClick: (row: T) => void
  show?: (row: T) => boolean
  disabled?: (row: T) => boolean
}

export interface DataTablePagination {
  page: number
  pageSize: number
  total: number
}

export interface EmptyStateConfig {
  title: string
  description?: string
  icon?: ReactNode
  action?: ReactNode
}

export interface DataTableProps<T> {
  /** Data rows */
  data: T[]
  /** Column definitions */
  columns: ColumnDef<T>[]
  /** Extract unique ID from a row */
  getRowId: (row: T) => string
  /** Pagination state */
  pagination?: DataTablePagination
  onPageChange?: (page: number) => void
  /** Sorting */
  sortable?: boolean
  defaultSort?: { column: string; direction: 'asc' | 'desc' }
  onSort?: (column: string, direction: 'asc' | 'desc') => void
  /** Row selection */
  selectable?: boolean
  selectedRows?: string[]
  onSelectionChange?: (ids: string[]) => void
  /** Bulk actions (shown when rows selected) */
  bulkActions?: BulkAction[]
  /** Per-row actions */
  rowActions?: RowAction<T>[]
  /** Row click handler */
  onRowClick?: (row: T) => void
  /** Active/selected row ID for highlight */
  activeRowId?: string
  /** Mobile card renderer (replaces table on small screens) */
  mobileCardRenderer?: (row: T, actions?: RowAction<T>[]) => ReactNode
  /** Search */
  searchable?: boolean
  searchPlaceholder?: string
  searchValue?: string
  onSearch?: (query: string) => void
  /** Loading state */
  loading?: boolean
  /** Empty state */
  emptyState?: EmptyStateConfig
  /** Additional class */
  className?: string
}

// --- Component ---

export function DataTable<T>({
  data,
  columns,
  getRowId,
  pagination,
  onPageChange,
  sortable = false,
  defaultSort,
  onSort,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  bulkActions,
  rowActions,
  onRowClick,
  activeRowId,
  mobileCardRenderer,
  searchable = false,
  searchPlaceholder = 'Search...',
  searchValue = '',
  onSearch,
  loading = false,
  emptyState,
  className,
}: DataTableProps<T>) {
  const [sortState, setSortState] = useState(
    defaultSort || { column: '', direction: 'asc' as const },
  )

  const totalPages = pagination
    ? Math.ceil(pagination.total / pagination.pageSize) || 1
    : 1

  const handleSort = useCallback(
    (columnKey: string) => {
      const newDirection =
        sortState.column === columnKey && sortState.direction === 'asc'
          ? 'desc'
          : 'asc'
      const next = { column: columnKey, direction: newDirection as 'asc' | 'desc' }
      setSortState(next)
      onSort?.(next.column, next.direction)
    },
    [sortState, onSort],
  )

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return
    const allIds = data.map(getRowId)
    const allSelected = allIds.every((id) => selectedRows.includes(id))
    onSelectionChange(allSelected ? [] : allIds)
  }, [data, getRowId, selectedRows, onSelectionChange])

  const handleSelectRow = useCallback(
    (id: string) => {
      if (!onSelectionChange) return
      const next = selectedRows.includes(id)
        ? selectedRows.filter((r) => r !== id)
        : [...selectedRows, id]
      onSelectionChange(next)
    },
    [selectedRows, onSelectionChange],
  )

  const allSelected =
    data.length > 0 && data.every((row) => selectedRows.includes(getRowId(row)))

  return (
    <div className={clsx('bg-surface rounded-card border border-hairline overflow-hidden', className)}>
      {/* Top bar: search + bulk actions */}
      {(searchable || (selectable && selectedRows.length > 0)) && (
        <div className="px-4 py-3.5 border-b border-hairline flex items-center justify-between gap-4">
          {searchable && onSearch && (
            <div className="relative flex-1 max-w-sm">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-faint" />
              <input
                type="text"
                value={searchValue}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={searchPlaceholder}
                className="w-full pl-9 pr-3 py-2 text-body-sm border border-hairline rounded-input bg-surface text-ink placeholder:text-ink-faint focus:outline-none focus:border-accent focus:shadow-focus transition-colors duration-fast"
              />
            </div>
          )}

          {selectable && selectedRows.length > 0 && (
            <div className="flex items-center gap-2">
              <span className="text-body-sm text-ink-muted">
                {selectedRows.length} selected
              </span>
              {bulkActions?.map((action) => (
                <button
                  key={action.label}
                  onClick={() => action.onAction(selectedRows)}
                  className={clsx(
                    'inline-flex items-center gap-1.5 px-3 py-2 rounded-button text-body-sm font-medium transition-colors duration-fast',
                    action.variant === 'danger'
                      ? 'bg-danger-soft text-danger hover:bg-danger-soft/70'
                      : 'bg-accent-soft text-accent hover:bg-accent-soft/70',
                  )}
                >
                  {action.icon}
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* The table: from md up when phones get cards, at every width otherwise.
          A table wider than the screen scrolls inside its card; the card's
          rounded overflow-hidden used to cut the last columns off on phones. */}
      <div className={clsx(mobileCardRenderer ? 'hidden md:block' : 'block', 'overflow-x-auto')}>
        <table className="min-w-full divide-y divide-hairline">
          <thead>
            <tr className="bg-surface-alt">
              {selectable && (
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={handleSelectAll}
                    className="rounded border-hairline text-accent focus:ring-accent"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={clsx(
                    'px-4 py-3 text-overline text-ink-muted font-semibold',
                    col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left',
                    col.width,
                    col.hideOnMobile && 'hidden lg:table-cell',
                  )}
                >
                  {col.sortable && sortable ? (
                    <button
                      onClick={() => handleSort(col.key)}
                      className="inline-flex items-center gap-1 hover:text-ink transition-colors duration-fast group"
                    >
                      {col.header}
                      <span className="flex flex-col">
                        <ChevronUpIcon
                          className={clsx(
                            'h-3 w-3 -mb-1',
                            sortState.column === col.key && sortState.direction === 'asc'
                              ? 'text-accent'
                              : 'text-ink-faint/60 group-hover:text-ink-faint',
                          )}
                        />
                        <ChevronDownIcon
                          className={clsx(
                            'h-3 w-3',
                            sortState.column === col.key && sortState.direction === 'desc'
                              ? 'text-accent'
                              : 'text-ink-faint/60 group-hover:text-ink-faint',
                          )}
                        />
                      </span>
                    </button>
                  ) : (
                    col.header
                  )}
                </th>
              ))}
              {rowActions && rowActions.length > 0 && (
                <th className="px-4 py-3 w-24 text-right text-overline text-ink-muted font-semibold">
                  Actions
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {loading ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)}
                  className="px-4 py-12 text-center"
                >
                  <div className="w-6 h-6 spinner mx-auto" />
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0)}
                  className="px-4 py-12 text-center"
                >
                  <div className="max-w-sm mx-auto">
                    {emptyState?.icon && (
                      <div className="mb-3 text-ink-faint">{emptyState.icon}</div>
                    )}
                    <p className="font-outfit text-h4 text-ink">
                      {emptyState?.title || 'No data found'}
                    </p>
                    {emptyState?.description && (
                      <p className="mt-1.5 text-body-sm text-ink-muted">
                        {emptyState.description}
                      </p>
                    )}
                    {emptyState?.action && (
                      <div className="mt-4">{emptyState.action}</div>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row) => {
                const rowId = getRowId(row)
                const isSelected = selectedRows.includes(rowId)
                const isActive = activeRowId === rowId

                return (
                  <tr
                    key={rowId}
                    onClick={() => onRowClick?.(row)}
                    className={clsx(
                      'transition-colors',
                      onRowClick && 'cursor-pointer',
                      isActive
                        ? 'bg-accent-soft'
                        : isSelected
                          ? 'bg-accent-soft/40'
                          : 'hover:bg-surface-alt/50',
                    )}
                  >
                    {selectable && (
                      <td className="px-4 py-3 w-10">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            e.stopPropagation()
                            handleSelectRow(rowId)
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-hairline text-accent focus:ring-accent"
                        />
                      </td>
                    )}
                    {columns.map((col) => (
                      <td
                        key={col.key}
                        className={clsx(
                          'px-4 py-3.5 text-body-sm',
                          col.align === 'right' ? 'text-right' : col.align === 'center' ? 'text-center' : 'text-left',
                          col.hideOnMobile && 'hidden lg:table-cell',
                          col.width,
                        )}
                      >
                        {col.cell(row)}
                      </td>
                    ))}
                    {rowActions && rowActions.length > 0 && (
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {rowActions
                            .filter((action) => !action.show || action.show(row))
                            .map((action) => (
                              <button
                                key={action.label}
                                onClick={(e) => {
                                  e.stopPropagation()
                                  action.onClick(row)
                                }}
                                disabled={action.disabled?.(row)}
                                className="p-1.5 rounded-button text-ink-faint hover:text-accent hover:bg-surface-alt disabled:opacity-50 transition-colors duration-fast"
                                title={action.label}
                              >
                                {action.icon || (
                                  <span className="text-body-sm">{action.label}</span>
                                )}
                              </button>
                            ))}
                        </div>
                      </td>
                    )}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile card view */}
      {mobileCardRenderer && (
        <div className="md:hidden divide-y divide-hairline">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-6 h-6 spinner mx-auto" />
            </div>
          ) : data.length === 0 ? (
            <div className="p-8 text-center">
              <p className="font-outfit text-h4 text-ink">
                {emptyState?.title || 'No data found'}
              </p>
              {emptyState?.description && (
                <p className="mt-1.5 text-body-sm text-ink-muted">
                  {emptyState.description}
                </p>
              )}
            </div>
          ) : (
            data.map((row) => (
              <div
                key={getRowId(row)}
                onClick={() => onRowClick?.(row)}
                className={clsx(
                  'p-4',
                  onRowClick && 'cursor-pointer active:bg-surface-alt',
                  activeRowId === getRowId(row) && 'bg-accent-soft',
                )}
              >
                {mobileCardRenderer(row, rowActions)}
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.total > pagination.pageSize && (
        <div className="px-4 py-3.5 border-t border-hairline flex items-center justify-between">
          <p className="text-body-sm text-ink-muted">
            Showing{' '}
            <span className="font-medium text-ink">
              {(pagination.page - 1) * pagination.pageSize + 1}
            </span>{' '}
            to{' '}
            <span className="font-medium text-ink">
              {Math.min(pagination.page * pagination.pageSize, pagination.total)}
            </span>{' '}
            of{' '}
            <span className="font-medium text-ink">{pagination.total}</span>{' '}
            results
          </p>
          <div className="flex items-center gap-1">
            <button
              type="button"
              aria-label="Previous page"
              onClick={() => onPageChange?.(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="p-1.5 rounded-button border border-hairline text-ink-muted hover:bg-surface-alt disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-fast"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>

            <PageNumbers
              page={pagination.page}
              totalPages={totalPages}
              onPageChange={onPageChange}
            />

            <button
              type="button"
              aria-label="Next page"
              onClick={() => onPageChange?.(pagination.page + 1)}
              disabled={pagination.page >= totalPages}
              className="p-1.5 rounded-button border border-hairline text-ink-muted hover:bg-surface-alt disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-fast"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function PageNumbers({
  page,
  totalPages,
  onPageChange,
}: {
  page: number
  totalPages: number
  onPageChange?: (page: number) => void
}) {
  const pages: (number | 'ellipsis')[] = []

  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pages.push(i)
  } else {
    pages.push(1)
    if (page > 3) pages.push('ellipsis')
    for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
      pages.push(i)
    }
    if (page < totalPages - 2) pages.push('ellipsis')
    pages.push(totalPages)
  }

  return (
    <>
      {pages.map((p, i) =>
        p === 'ellipsis' ? (
          <span key={`e${i}`} className="px-1 text-ink-faint">
            ...
          </span>
        ) : (
          <button
            key={p}
            onClick={() => onPageChange?.(p)}
            className={clsx(
              'min-w-[32px] h-8 rounded-button text-body-sm font-medium transition-colors duration-fast',
              p === page
                ? 'bg-accent text-on-accent'
                : 'text-ink-muted hover:bg-surface-alt',
            )}
          >
            {p}
          </button>
        ),
      )}
    </>
  )
}
